import React from 'react';
import { CodeSnippetViewer } from '../../../questions';
import { getLanguageFromSkills } from '../../../utils/languageDetection';

const PreviousQuestionsAccordion = ({
  question, 
  collapsedSections, 
  toggleCollapse 
}) => {
  if (!question || question.is_main_question) return null;

  // Function to render answers based on question type
  const renderAnswer = (answer, questionType) => {
    // For empty answers, show a message
    if (!answer) {
      return <p className="text-gray-500 italic">No answer provided</p>;
    }
    
    // Don't show placeholder answers
    if (answer === "[USER ANSWER APPENDED HERE]") return null;
    
    // Define code question types that need syntax highlighting for their answers
    const codeWritingQuestionTypes = ['code-write', 'code-completion', 'code-refactoring'];
    
    // For multiple-answer questions, the answer might be an array
    if (questionType === 'multiple-answer' && Array.isArray(answer)) {
      return (
        <div className="flex flex-row flex-wrap gap-2">
          {answer.map((ans, index) => (
            <div key={index} className="px-4 py-2 bg-blue-100 rounded-full text-blue-700">
              {ans}
            </div>
          ))}
        </div>
      );
    } else if (questionType === 'single-answer' && typeof answer === 'string') {
      return (
        <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
          {answer}
        </div>
      );
    } else if (questionType === 'true-false' && typeof answer === 'string') {
      return (
        <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
          {answer}
        </div>
      );
    } else if (['long-text', 'short-text', 'code-explanation', 'find-the-bug', 'security-task', 'performance-optimization'].includes(questionType) && typeof answer === 'string') {
      return (
        <p className="text-gray-800 bg-blue-50 p-3 rounded-md whitespace-pre-wrap">
          {answer}
        </p>
      );
    }
    // Handle code writing questions that need syntax highlighting for their answers
    else if (codeWritingQuestionTypes.includes(questionType)) {
      return (
        <div className="overflow-x-auto rounded-md">
          <CodeSnippetViewer 
            code={answer}
            skillsTested={question.skillsTested}
          />
        </div>
      );
    }
    
    // Fallback for any other type of answer
    return (
      <p className="text-gray-800">
        {typeof answer === 'object' && answer !== null
          ? (answer.text 
              ? answer.text 
              : JSON.stringify(answer))
          : (answer !== null && answer !== undefined
              ? answer.toString()
              : 'No answer provided')}
      </p>
    );
  };

  return (
    <div className="mb-4">
      {/* Show collapsible main question if this is a followup */}
      <div>
        <div className="border-t border-gray-200">
          <div 
            className="cursor-pointer hover:bg-gray-50 transition-colors duration-200 group"
            onClick={() => toggleCollapse('mainQuestion')}
          >
            <div className={`flex items-center justify-between w-full text-left py-4 transition-all duration-200 ${
              !collapsedSections.mainQuestion ? 'px-4' : 'group-hover:px-4'
            }`}>
              <div className="flex items-center">
                <div className="bg-[#33C6B6] text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <h4 className="text-sm text-gray-600">
                  <span className="font-bold">Main Question</span> (Answered)
                </h4>
              </div>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  collapsedSections.mainQuestion ? 'rotate-0' : 'rotate-90'
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            {!collapsedSections.mainQuestion && (
              <div className="mt-3 ml-9 pb-4 px-4">
                <p className="text-lg text-gray-700 mb-4"><span className="font-bold">Q: </span>{question.questionText}</p>
                
                {/* Display code snippet if available for main question */}
                {question.codeSnippet && (
                  <div className="mb-4">
                    <CodeSnippetViewer 
                      code={question.codeSnippet}
                      skillsTested={question.skillsTested}
                    />
                  </div>
                )}
                
                <div className="pl-4 border-l-4 border-blue-200 py-2">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
                  {renderAnswer(question.answer, question.type)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Show all previously answered followup questions if this is a followup */}
      {question.followup_questions_and_answers && (
        <div className="space-y-0">
          {question.followup_questions_and_answers.slice(0, question.current_followup_index).map((followup, index) => (
            <div key={index}>
              <div className="border-t border-gray-200">
                <div 
                  className="cursor-pointer hover:bg-gray-50 transition-colors duration-200 group"
                  onClick={() => toggleCollapse('followupQuestion', index)}
                >
                  <div className={`flex items-center justify-between w-full text-left py-4 transition-all duration-200 ${
                    collapsedSections.followupQuestions[index] ? 'px-4' : 'group-hover:px-4'
                  }`}>
                    <div className="flex items-center">
                      <div className="bg-[#33C6B6] text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <h4 className="text-sm text-gray-600">
                        <span className="font-bold">Follow-up Question {index + 1}</span> (Answered)
                      </h4>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                        collapsedSections.followupQuestions[index] ? 'rotate-90' : 'rotate-0'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  {collapsedSections.followupQuestions[index] && (
                    <div className="mt-3 ml-9 pb-4 px-4">
                      <p className="text-lg text-gray-700 mb-4"><span className="font-bold">Q: </span>{followup.question_text}</p>
                      
                      
                      {/* Display code snippet if available for followup question */}
                      {followup.codeSnippet && (
                        <div className="mb-4">
                          <CodeSnippetViewer 
                            code={followup.codeSnippet}
                            skillsTested={followup.skillsTested}
                          />
                        </div>
                      )}
                      
                      <div className="pl-4 border-l-4 border-blue-200 py-2">
                        <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
                        {renderAnswer(followup.answer_text, followup.type)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Separator border between previous questions and current question */}
      <div className="border-t border-gray-200 mb-4 pt-4"></div>
    </div>
  );
};

export default PreviousQuestionsAccordion;
